import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, PackageCheck, History, Truck,
  Package, SendHorizonal, CheckCircle2, Clock, FileText,
  Tag, Printer, AlertCircle,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import type { BonLivraison, LigneBonLivraison, EntityEvent } from "@shared/schema";
import { useEffect, useState, useRef } from "react";
import { printBonLivraison } from "./print";

const STATUT_LABELS: Record<string, string> = {
  a_preparer: "À préparer",
  en_preparation: "En préparation",
  prete: "Prête",
  livree_partielle: "Livrée partiellement",
  livree: "Livrée",
  a_facturer: "À facturer",
  cloturee: "Clôturée",
};

const STATUT_ORDER = [
  "a_preparer", "en_preparation", "prete",
  "livree_partielle", "livree", "a_facturer", "cloturee",
] as const;

const STATUT_CLASSES: Record<string, string> = {
  a_preparer: "bg-secondary text-secondary-foreground",
  en_preparation: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  prete: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  livree_partielle: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  livree: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  a_facturer: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  cloturee: "bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
};

type HistoriqueEntry = EntityEvent & { actorNom: string | null };

interface ExpeditionItem {
  ligneIndex: number;
  qteLivreeNow: number;
  sn: string[];
}

function getCommandee(l: LigneBonLivraison): number {
  return (l as any).quantiteCommandee ?? l.quantite ?? 0;
}
function getLivree(l: LigneBonLivraison): number {
  return (l as any).quantiteLivree ?? 0;
}
function getReste(l: LigneBonLivraison): number {
  return (l as any).resteALivrer ?? Math.max(0, getCommandee(l) - getLivree(l));
}

export default function BonLivraisonDetail() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: bl, isLoading } = useQuery<BonLivraison>({
    queryKey: ["/api/bons-livraison", id],
    queryFn: async () => {
      const res = await authFetch(`/api/bons-livraison/${id}`);
      if (!res.ok) throw new Error("Bon de livraison introuvable");
      return res.json();
    },
    enabled: !!id,
  });

  const { data: historique = [] } = useQuery<HistoriqueEntry[]>({
    queryKey: ["/api/bons-livraison", id, "historique"],
    queryFn: async () => {
      const res = await authFetch(`/api/bons-livraison/${id}/historique`);
      if (!res.ok) throw new Error("Erreur historique");
      return res.json();
    },
    enabled: !!id,
  });

  const [transporteur, setTransporteur] = useState("");
  const [datePassage, setDatePassage] = useState("");
  const [numeroSuivi, setNumeroSuivi] = useState("");
  const [nbColis, setNbColis] = useState("");
  const [nbPalettes, setNbPalettes] = useState("");

  const { data: logoData } = useQuery<{ url?: string }>({
    queryKey: ["/api/settings/logo"],
    queryFn: async () => (await authFetch("/api/settings/logo")).json(),
    staleTime: 60_000,
  });

  const [showExpeditionForm, setShowExpeditionForm] = useState(false);
  const [showFactuDialog, setShowFactuDialog] = useState(false);
  const lastRequestedStatut = useRef<string | null>(null);
  const [expeditionDate, setExpeditionDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [expeditionItems, setExpeditionItems] = useState<ExpeditionItem[]>([]);

  useEffect(() => {
    if (bl) {
      setTransporteur(bl.transporteur || "");
      setDatePassage(
        bl.datePassageTransporteur
          ? new Date(bl.datePassageTransporteur).toISOString().slice(0, 10)
          : ""
      );
      setNumeroSuivi(bl.numeroSuivi || "");
      setNbColis(bl.nbColis != null ? String(bl.nbColis) : "");
      setNbPalettes((bl as any).nbPalettes != null ? String((bl as any).nbPalettes) : "");
    }
  }, [bl]);

  useEffect(() => {
    if (showExpeditionForm && bl) {
      const lignes = (Array.isArray(bl.lignes) ? bl.lignes : []) as LigneBonLivraison[];
      const items: ExpeditionItem[] = lignes
        .map((l, i) => ({ ligneIndex: i, qteLivreeNow: getReste(l), sn: [] }))
        .filter((item) => item.qteLivreeNow > 0);
      setExpeditionItems(items);
    }
  }, [showExpeditionForm]);

  const saveMutation = useMutation({
    mutationFn: async (payload: any) => {
      const res = await authFetch(`/api/bons-livraison/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erreur d'enregistrement");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bons-livraison", id] });
      toast({ title: "Modifications enregistrées" });
    },
    onError: (e: any) => {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    },
  });

  const statutMutation = useMutation({
    mutationFn: async (statut: string) => {
      lastRequestedStatut.current = statut;
      const res = await authFetch(`/api/bons-livraison/${id}/statut`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ statut }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erreur de changement de statut");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bons-livraison", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/bons-livraison", id, "historique"] });
      queryClient.invalidateQueries({ queryKey: ["/api/bons-livraison"] });
      toast({ title: "Statut mis à jour" });
      if (lastRequestedStatut.current === "livree" && !(bl as any)?.factuationDemandeAt) {
        setShowFactuDialog(true);
      }
    },
    onError: (e: any) => {
      toast({
        title: "Impossible de changer le statut",
        description: e.message,
        variant: "destructive",
      });
    },
  });

  const expeditionMutation = useMutation({
    mutationFn: async (payload: {
      lignesExpedition: any[];
      dateExpedition: string;
    }) => {
      const res = await authFetch(`/api/bons-livraison/${id}/expedition`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erreur d'enregistrement de l'expédition");
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/bons-livraison", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/bons-livraison", id, "historique"] });
      queryClient.invalidateQueries({ queryKey: ["/api/bons-livraison"] });
      setShowExpeditionForm(false);
      toast({ title: "Expédition enregistrée" });
      if (data?.statut === "livree" && !(bl as any)?.factuationDemandeAt) {
        setShowFactuDialog(true);
      }
    },
    onError: (e: any) => {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    },
  });

  const demanderEtiquetteMutation = useMutation({
    mutationFn: async () => {
      const res = await authFetch(`/api/bons-livraison/${id}/demander-etiquette`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erreur");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bons-livraison", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/count"] });
      toast({ title: "Demande envoyée", description: "Lucie Wolfarth a été notifiée pour générer l'étiquette." });
    },
    onError: (e: any) => {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    },
  });

  const passerEnFacturationMutation = useMutation({
    mutationFn: async () => {
      const res = await authFetch(`/api/bons-livraison/${id}/passer-en-facturation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erreur");
      }
      return res.json();
    },
    onSuccess: () => {
      setShowFactuDialog(false);
      queryClient.invalidateQueries({ queryKey: ["/api/bons-livraison", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/bons-livraison", id, "historique"] });
      queryClient.invalidateQueries({ queryKey: ["/api/bons-livraison"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/count"] });
      toast({ title: "Passé en facturation", description: "Lucie Wolfarth a été notifiée pour éditer la facture dans Zoho." });
    },
    onError: (e: any) => {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    },
  });

  const saveLogistique = () =>
    saveMutation.mutate({
      transporteur: transporteur || null,
      datePassageTransporteur: datePassage || null,
      numeroSuivi: numeroSuivi || null,
      nbColis: nbColis === "" ? null : Number(nbColis),
      nbPalettes: nbPalettes === "" ? null : Number(nbPalettes),
    });

  const updateExpeditionQte = (ligneIndex: number, value: string) => {
    const qte = Math.max(0, Number(value));
    setExpeditionItems((prev) =>
      prev.map((item) =>
        item.ligneIndex !== ligneIndex
          ? item
          : { ...item, qteLivreeNow: qte, sn: item.sn.slice(0, qte) }
      )
    );
  };

  const updateExpeditionSn = (ligneIndex: number, snIndex: number, value: string) => {
    setExpeditionItems((prev) =>
      prev.map((item) => {
        if (item.ligneIndex !== ligneIndex) return item;
        const sn = [...item.sn];
        sn[snIndex] = value;
        return { ...item, sn };
      })
    );
  };

  const submitExpedition = () => {
    const nonZero = expeditionItems.filter((i) => i.qteLivreeNow > 0);
    if (nonZero.length === 0) {
      toast({ title: "Aucune quantité saisie", variant: "destructive" });
      return;
    }
    expeditionMutation.mutate({
      lignesExpedition: nonZero.map((i) => ({
        ligneIndex: i.ligneIndex,
        qteLivreeNow: i.qteLivreeNow,
        sn: i.sn,
      })),
      dateExpedition: expeditionDate,
    });
  };

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">Chargement…</div>;
  }

  if (!bl) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Bon de livraison introuvable.</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate("/preparation")}
          data-testid="button-back-devis"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour à la préparation
        </Button>
      </div>
    );
  }

  const lignes = (Array.isArray(bl.lignes) ? bl.lignes : []) as LigneBonLivraison[];
  const lignesAvecReste = lignes.filter((l) => getReste(l) > 0);
  const lignesLivrees = lignes.filter((l) => getLivree(l) > 0);
  const peutExpedier = ["en_preparation", "prete", "livree_partielle"].includes(bl.statut);

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/preparation")}
            data-testid="button-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1
              className="text-xl font-semibold flex items-center gap-2"
              data-testid="text-numero-bl"
            >
              <PackageCheck className="w-5 h-5 text-primary" /> {bl.numeroBl}
            </h1>
            <p className="text-sm text-muted-foreground">Préparation de la commande</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => printBonLivraison(bl as any, logoData?.url)}
            data-testid="button-print-bl"
          >
            <FileText className="w-4 h-4 mr-2" /> Générer PDF
          </Button>
          {bl.statut === "prete" && !(bl as any).etiquetteDemandeAt && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => demanderEtiquetteMutation.mutate()}
              disabled={demanderEtiquetteMutation.isPending}
              data-testid="button-demander-etiquette"
            >
              <Tag className="w-4 h-4 mr-2" />
              {demanderEtiquetteMutation.isPending ? "Envoi…" : "Demander l'étiquette"}
            </Button>
          )}
          <Badge
            className={STATUT_CLASSES[bl.statut] || ""}
            data-testid="badge-statut-bl"
          >
            {STATUT_LABELS[bl.statut] || bl.statut}
          </Badge>
          <Select
            value={bl.statut}
            onValueChange={(v) => statutMutation.mutate(v)}
            disabled={statutMutation.isPending}
          >
            <SelectTrigger className="w-[220px]" data-testid="select-statut-bl">
              <SelectValue placeholder="Changer le statut" />
            </SelectTrigger>
            <SelectContent>
              {STATUT_ORDER.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUT_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Infos devis ── */}
      <div className="border rounded-lg p-4">
        <h2 className="text-sm font-medium mb-1">Informations devis</h2>
        <p className="text-sm text-muted-foreground">
          Devis associé : {bl.devisId ? bl.devisId.slice(0, 8) : "—"}
        </p>
      </div>

      {/* ── Handoffs — état des demandes ── */}
      {((bl as any).etiquetteDemandeAt || (bl as any).factuationDemandeAt) && (
        <div className="border rounded-lg p-4 space-y-2">
          <h2 className="text-sm font-medium flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-muted-foreground" /> Suivi des demandes
          </h2>
          {(bl as any).etiquetteDemandeAt && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="text-etiquette-demandee">
              <Tag className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <span>
                Étiquette demandée le{" "}
                <strong className="text-foreground">
                  {new Date((bl as any).etiquetteDemandeAt).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </strong>
              </span>
            </div>
          )}
          {(bl as any).factuationDemandeAt && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="text-facturation-demandee">
              <Printer className="w-3.5 h-3.5 text-purple-500 shrink-0" />
              <span>
                Facturation demandée le{" "}
                <strong className="text-foreground">
                  {new Date((bl as any).factuationDemandeAt).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </strong>
              </span>
            </div>
          )}
        </div>
      )}

      {/* ── Section Commandé ── */}
      <div className="border rounded-lg p-4 space-y-3">
        <h2 className="text-sm font-medium flex items-center gap-2">
          <Package className="w-4 h-4 text-muted-foreground" /> Commandé
        </h2>
        {lignes.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucune ligne.</p>
        ) : (
          <div className="divide-y text-sm">
            {lignes.map((l, i) => {
              const qteCmd = getCommandee(l);
              const qteLiv = getLivree(l);
              const reste = getReste(l);
              return (
                <div
                  key={i}
                  className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1"
                  data-testid={`row-ligne-bl-${i}`}
                >
                  <div>
                    <p className="font-medium">{l.designation}</p>
                    <p className="text-xs text-muted-foreground">{l.sku}</p>
                  </div>
                  <div className="flex items-center gap-4 flex-wrap text-xs">
                    <span className="text-muted-foreground">
                      Commandé : <strong className="text-foreground">{qteCmd}</strong>
                    </span>
                    <span
                      className={
                        qteLiv > 0
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-muted-foreground"
                      }
                    >
                      Livré : <strong>{qteLiv}</strong>
                    </span>
                    {reste > 0 && (
                      <span className="text-orange-600 dark:text-orange-400 font-semibold">
                        Reste : {reste}
                      </span>
                    )}
                    {reste === 0 && qteLiv > 0 && (
                      <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Complet
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Section Livré ── */}
      {lignesLivrees.length > 0 && (
        <div className="border rounded-lg p-4 space-y-3">
          <h2 className="text-sm font-medium flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Livré
          </h2>
          <div className="divide-y text-sm">
            {lignesLivrees.map((l, i) => {
              const origIndex = lignes.indexOf(l);
              const sn = Array.isArray((l as any).sn) ? ((l as any).sn as string[]) : [];
              return (
                <div
                  key={i}
                  className="py-3 space-y-1.5"
                  data-testid={`row-livree-${origIndex}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="font-medium">{l.designation}</p>
                      <p className="text-xs text-muted-foreground">{l.sku}</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs flex-wrap">
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                        Qté livrée : {getLivree(l)}
                      </span>
                      {(l as any).dateExpedition && (
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date((l as any).dateExpedition).toLocaleDateString("fr-FR")}
                        </span>
                      )}
                    </div>
                  </div>
                  {sn.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {sn.map((s, si) => (
                        <Badge
                          key={si}
                          variant="outline"
                          className="text-xs font-mono"
                          data-testid={`badge-sn-livree-${origIndex}-${si}`}
                        >
                          {s}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Section Reste à livrer ── */}
      {lignesAvecReste.length > 0 && (
        <div className="border border-orange-200 dark:border-orange-800 rounded-lg p-4 space-y-3 bg-orange-50/50 dark:bg-orange-950/20">
          <h2 className="text-sm font-medium flex items-center gap-2 text-orange-700 dark:text-orange-400">
            <Clock className="w-4 h-4" /> Reste à livrer
          </h2>
          <div className="divide-y divide-orange-100 dark:divide-orange-900 text-sm">
            {lignesAvecReste.map((l, i) => (
              <div
                key={i}
                className="py-2 flex items-center justify-between"
                data-testid={`row-reste-${i}`}
              >
                <div>
                  <p className="font-medium">{l.designation}</p>
                  <p className="text-xs text-muted-foreground">{l.sku}</p>
                </div>
                <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                  {getReste(l)} restant{getReste(l) > 1 ? "s" : ""}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Bouton ouvrir formulaire expédition ── */}
      {peutExpedier && lignesAvecReste.length > 0 && !showExpeditionForm && (
        <Button
          onClick={() => setShowExpeditionForm(true)}
          data-testid="button-open-expedition"
        >
          <SendHorizonal className="w-4 h-4 mr-2" /> Enregistrer une expédition
        </Button>
      )}

      {/* ── Formulaire d'expédition ── */}
      {showExpeditionForm && (
        <div className="border-2 border-primary/30 rounded-lg p-4 space-y-4 bg-primary/5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-primary">Nouvelle expédition</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowExpeditionForm(false)}
              data-testid="button-cancel-expedition"
            >
              Annuler
            </Button>
          </div>

          <div className="space-y-1">
            <Label htmlFor="expedition-date">Date d'expédition</Label>
            <Input
              id="expedition-date"
              type="date"
              value={expeditionDate}
              onChange={(e) => setExpeditionDate(e.target.value)}
              className="max-w-xs"
              data-testid="input-expedition-date"
            />
          </div>

          <div className="space-y-3">
            {expeditionItems.map((item) => {
              const ligne = lignes[item.ligneIndex];
              if (!ligne) return null;
              const resteMax = getReste(ligne);
              const requiresSn =
                ligne.produitType === "robot" || ligne.produitType === "machine";
              return (
                <div
                  key={item.ligneIndex}
                  className="border rounded p-3 space-y-2 bg-background"
                  data-testid={`row-expedition-${item.ligneIndex}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="text-sm font-medium">{ligne.designation}</p>
                      <p className="text-xs text-muted-foreground">
                        {ligne.sku} · Reste : {resteMax}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label
                        htmlFor={`qte-${item.ligneIndex}`}
                        className="text-xs whitespace-nowrap"
                      >
                        À expédier :
                      </Label>
                      <Input
                        id={`qte-${item.ligneIndex}`}
                        type="number"
                        min="0"
                        max={resteMax}
                        value={item.qteLivreeNow}
                        onChange={(e) =>
                          updateExpeditionQte(item.ligneIndex, e.target.value)
                        }
                        className="w-20"
                        data-testid={`input-qte-expedition-${item.ligneIndex}`}
                      />
                    </div>
                  </div>
                  {requiresSn && item.qteLivreeNow > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">
                        N° de série ({item.qteLivreeNow} attendu
                        {item.qteLivreeNow > 1 ? "s" : ""}) :
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {Array.from({ length: item.qteLivreeNow }).map((_, snIdx) => (
                          <Input
                            key={snIdx}
                            value={item.sn[snIdx] || ""}
                            onChange={(e) =>
                              updateExpeditionSn(item.ligneIndex, snIdx, e.target.value)
                            }
                            placeholder={`N° série #${snIdx + 1}`}
                            data-testid={`input-sn-expedition-${item.ligneIndex}-${snIdx}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <Button
            onClick={submitExpedition}
            disabled={expeditionMutation.isPending}
            data-testid="button-submit-expedition"
          >
            <SendHorizonal className="w-4 h-4 mr-2" />
            {expeditionMutation.isPending ? "Enregistrement…" : "Valider l'expédition"}
          </Button>
        </div>
      )}

      {/* ── Transport ── */}
      <div className="border rounded-lg p-4 space-y-4">
        <h2 className="text-sm font-medium flex items-center gap-2">
          <Truck className="w-4 h-4" /> Transport
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label htmlFor="transporteur">Transporteur</Label>
            <Input
              id="transporteur"
              value={transporteur}
              onChange={(e) => setTransporteur(e.target.value)}
              data-testid="input-transporteur"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="date-passage">Date de passage</Label>
            <Input
              id="date-passage"
              type="date"
              value={datePassage}
              onChange={(e) => setDatePassage(e.target.value)}
              data-testid="input-date-passage"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="numero-suivi">Numéro de suivi</Label>
            <Input
              id="numero-suivi"
              value={numeroSuivi}
              onChange={(e) => setNumeroSuivi(e.target.value)}
              data-testid="input-numero-suivi"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="nb-colis">Nombre de colis</Label>
            <Input
              id="nb-colis"
              type="number"
              min="0"
              value={nbColis}
              onChange={(e) => setNbColis(e.target.value)}
              data-testid="input-nb-colis"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="nb-palettes">Nombre de palettes</Label>
            <Input
              id="nb-palettes"
              type="number"
              min="0"
              value={nbPalettes}
              onChange={(e) => setNbPalettes(e.target.value)}
              data-testid="input-nb-palettes"
            />
          </div>
        </div>
        <Button
          size="sm"
          onClick={saveLogistique}
          disabled={saveMutation.isPending}
          data-testid="button-save-logistique"
        >
          Enregistrer
        </Button>
      </div>

      {/* ── Dialog : Passer en facturation ? ── */}
      <Dialog open={showFactuDialog} onOpenChange={setShowFactuDialog}>
        <DialogContent data-testid="dialog-passer-facturation">
          <DialogHeader>
            <DialogTitle>Passer en facturation ?</DialogTitle>
            <DialogDescription>
              Le bon de livraison <strong>{bl.numeroBl}</strong> est entièrement livré.
              Voulez-vous le passer en facturation et notifier Lucie Wolfarth pour éditer la facture dans Zoho ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowFactuDialog(false)}
              data-testid="button-cancel-facturation"
            >
              Plus tard
            </Button>
            <Button
              onClick={() => passerEnFacturationMutation.mutate()}
              disabled={passerEnFacturationMutation.isPending}
              data-testid="button-confirm-facturation"
            >
              {passerEnFacturationMutation.isPending ? "En cours…" : "Oui, passer en facturation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Historique ── */}
      <div className="border rounded-lg p-4 space-y-3">
        <h2 className="text-sm font-medium flex items-center gap-2">
          <History className="w-4 h-4" /> Historique
        </h2>
        {historique.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun événement.</p>
        ) : (
          <div className="space-y-2">
            {historique.map((h) => {
              const isExpedition = (h.metadata as any)?.type === "expedition";
              const isSameState = h.fromState === h.toState;
              return (
                <div
                  key={h.id}
                  className="flex items-center justify-between text-sm border-b last:border-0 pb-2 last:pb-0"
                  data-testid={`row-historique-${h.id}`}
                >
                  <span>
                    {isExpedition || isSameState ? (
                      <>
                        <span className="font-medium">Expédition enregistrée</span>
                        {h.actorNom ? ` par ${h.actorNom}` : ""}
                      </>
                    ) : (
                      <>
                        {STATUT_LABELS[h.fromState || ""] || h.fromState || "—"} →{" "}
                        <strong>
                          {STATUT_LABELS[h.toState || ""] || h.toState}
                        </strong>
                        {h.actorNom ? ` par ${h.actorNom}` : ""}
                      </>
                    )}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {h.createdAt
                      ? new Date(h.createdAt).toLocaleString("fr-FR")
                      : ""}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
